/**
 * Withdrawal Flow Controller
 * 
 * Orchestrates the entire withdrawal flow by coordinating modal, bonus selection, and payment.
 * Follows SOLID principles - acts as a facade/coordinator for withdrawal functionality.
 * 
 * @package betbeast
 */

import { WithdrawalModal } from './withdrawal-modal.js';
import { WithdrawalHandler } from './withdrawal-handler.js';
import { WithdrawalMethodSelector } from './withdrawal-method-selector.js';
import { WithdrawalMethodsSlider } from './withdrawal-methods-slider.js';
import { AmountInputValidator } from '../../modules/amount-input-validator.js';
import { WithdrawalBonusWarningModal } from './withdrawal-bonus-warning-modal.js';
import { WithdrawalCurrencyDisplayManager } from './withdrawal-currency-display-manager.js';
import { WithdrawalSubmitHandler } from './withdrawal-submit-handler.js';
import { WithdrawalSchemaRenderer } from './withdrawal-schema-renderer.js';
import { FloatingLabel } from '../../auth-flow-components/floating-label.js';

export class WithdrawalFlowController {
    constructor() {
        this.modal = null;
        this.bonusWarningModal = null;
        this.withdrawalHandler = null;
        this.withdrawalMethodSelector = null;
        this.withdrawalMethodsSlider = null;
        this.withdrawalInputValidator = null;
        this.withdrawalCurrencyDisplayManager = null;
        this.withdrawalSchemaRenderer = null;
        this.withdrawalSubmitHandler = null;
        this.floatingLabel = null;
        this.initialized = false;
    }
    
    /**
     * Initialize the withdrawal flow
     */
    init() {
        const provider = ((window.accountData && window.accountData.cashierProvider) || '').toLowerCase();
        if (provider === 'paymentiq') {
            return;
        }

        const modalElement = document.getElementById('wallet-withdrawal-modal');
        if (!modalElement) {
            console.warn('Wallet withdrawal modal not found in DOM');
            return;
        }
        
        // Initialize bonus warning modal (if exists)
        this.bonusWarningModal = new WithdrawalBonusWarningModal();
        this.bonusWarningModal.init();
        
        // Initialize modal manager
        this.modal = new WithdrawalModal('wallet-withdrawal-modal');
        
        // Initialize currency display manager (before method selector)
        this.withdrawalCurrencyDisplayManager = new WithdrawalCurrencyDisplayManager(modalElement);

        // Initialize schema renderer (before method selector so it can be passed in)
        this.withdrawalSchemaRenderer = new WithdrawalSchemaRenderer(modalElement);
        
        // Initialize withdrawal method selector (pass currency manager + schema renderer)
        this.withdrawalMethodSelector = new WithdrawalMethodSelector(
            modalElement,
            this.withdrawalCurrencyDisplayManager,
            this.withdrawalSchemaRenderer
        );
        
        // Initialize withdrawal methods slider (mobile)
        this.withdrawalMethodsSlider = new WithdrawalMethodsSlider(modalElement);
        
        // Initialize floating labels
        this.floatingLabel = new FloatingLabel(modalElement);
        
        // Initialize input validator
        const balanceStr = modalElement.getAttribute('data-wallet-balance');
        this.withdrawalInputValidator = new AmountInputValidator(modalElement, {
            inputSelector:    '.wallet-withdrawal-amount-input',
            wrapperSelector:  '.wallet-withdrawal-amount-input-wrapper',
            emptyMessage:     'Please enter a withdrawal amount',
            label:            'withdrawal',
            availableBalance: parseFloat(balanceStr) || 0
        });
        
        // Initialize withdrawal handler
        this.withdrawalHandler = new WithdrawalHandler(modalElement);
        
        // Initialize submit handler (pass controller + schema renderer)
        this.withdrawalSubmitHandler = new WithdrawalSubmitHandler(
            modalElement,
            this,
            this.withdrawalSchemaRenderer
        );
        
        // Check if we're on the withdrawal page and should auto-open
        this.handleAutoOpen();
        
        // Initialize withdrawal form
        this.withdrawalHandler.updateWithdrawalForm();
        
        this.initialized = true;
    }
    
    /**
     * Handle auto-opening modal on withdrawal page
     */
    handleAutoOpen() {
        const currentPath = window.location.pathname;
        
        // Check if we're on the withdrawal page
        if (currentPath.includes('/your-account/my-wallet/withdraw')) {
            // Open the modal (with bonus warning check)
            this.openModal();
        }
    }
    
    /**
     * Open the withdrawal modal (with bonus warning check)
     */
    openModal() {
        // Check if user has active bonuses
        if (this.bonusWarningModal && this.bonusWarningModal.exists()) {
            // Show bonus warning first
            this.bonusWarningModal.open(
                // On proceed - open withdrawal modal
                () => {
                    if (this.modal) {
                        this.modal.open();
                    }
                },
                // On cancel - do nothing (stay on wallet page)
                () => {
                    console.log('User cancelled withdrawal due to active bonuses');
                }
            );
        } else {
            // No active bonuses - open withdrawal modal directly
            if (this.modal) {
                this.modal.open();
            }
        }
    }
    
    /**
     * Close the withdrawal modal
     */
    closeModal() {
        if (this.modal) {
            this.modal.close();
        }
    }
    
    /**
     * Check if initialized
     */
    isInitialized() {
        return this.initialized;
    }
    
    /**
     * Get withdrawal input validator instance
     * @returns {AmountInputValidator}
     */
    getValidator() {
        return this.withdrawalInputValidator;
    }
    
    /**
     * Get withdrawal handler instance
     * @returns {WithdrawalHandler}
     */
    getHandler() {
        return this.withdrawalHandler;
    }
    
    /**
     * Get selected payment method
     * @returns {string|null}
     */
    getSelectedPaymentMethod() {
        return this.withdrawalMethodSelector ? this.withdrawalMethodSelector.getSelectedMethod() : null;
    }
}

// Create and export singleton instance
export const withdrawalFlow = new WithdrawalFlowController();
